// Standalone offer detail page — for offers not connected to a campaign/project.
// Uses Firebase auth (via memberId) instead of the offer URL JWT.

import { Building2, Eye, Paintbrush, Store } from "lucide-react";

import { BoostRewardsSection } from "@/components/boosts";
import { ProjectPageHeader } from "@/components/projects/project-page-header";
import {
  AffiliateLinkCard,
  CreateDeepLinkDialog,
  OfferStatsDisplay,
  PaymentsTab,
  PayoutBreakdown,
  PayoutTierDetails,
  PromoCodeCard,
  ShareOfferDialog,
} from "@/components/promo-codes";
import {
  isLinkOffer,
  isPromoOffer,
  useAffiliateOfferByMemberQuery,
} from "@/lib/queries/promo-codes";
import {
  Button,
  Card,
  CardContent,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@repo/ui";
import {
  Link,
  createFileRoute,
  notFound,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { TRPCClientError } from "@trpc/client";
import { useMemo } from "react";
import { z } from "zod";
import type { OrgRouteContext } from "@/lib/types/route-context";
import { useDateFilter } from "@/lib/hooks/use-date-filter";
import { DateRangeOption } from "@/lib/types/date-filter";
import { dateRangeToApiParams } from "@/lib/utils/date-range";

export const offerSearchSchema = z.object({
  memberId: z.coerce.number().int().positive(),
  tab: z.enum(["overview", "rewards", "payments"]).optional().catch(undefined),
  reward: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute("/(org)/o/$orgSlug/offers/$offerId/")({
  component: StandaloneOfferPage,
  validateSearch: offerSearchSchema,
});

function StandaloneOfferPage() {
  const { offerId, orgSlug } = Route.useParams();
  const { memberId, tab, reward } = Route.useSearch();
  const context = Route.useRouteContext() as OrgRouteContext;
  const { organization } = context;
  const location = useLocation();
  const navigate = useNavigate({ from: Route.fullPath });
  const currentTab = tab ?? "overview";

  const numericOfferId = Number(offerId);
  const isValidOfferId = Number.isFinite(numericOfferId) && numericOfferId > 0;

  // Date filter state (independent per tab)
  const overviewDateFilter = useDateFilter();
  const paymentsDateFilter = useDateFilter();

  const activeDateRange =
    currentTab === "payments"
      ? paymentsDateFilter.dateRange
      : overviewDateFilter.dateRange;

  // Convert date range to API parameters
  const dateParams = useMemo(
    () => dateRangeToApiParams(activeDateRange),
    [activeDateRange],
  );

  const {
    data: offerData,
    isPending,
    error,
    isFetching,
  } = useAffiliateOfferByMemberQuery(
    organization.id,
    memberId,
    isValidOfferId ? numericOfferId : 0,
    dateParams,
  );

  if (!isValidOfferId) {
    throw notFound();
  }

  const offer = offerData?.offer;
  const offerUrl = offerData?.offerUrl ?? "";
  const hasBoosts = (offer?.boosts.length ?? 0) > 0;
  const activeTab =
    tab === "rewards" && !hasBoosts ? "overview" : (tab ?? "overview");

  // Preview link for Landing Page banner
  const previewLink =
    offer?.affiliateShortLink ||
    (offer && isPromoOffer(offer)
      ? (offer.linkedAffiliateLink?.affiliateShortLink ?? null)
      : null);

  type TabValue = "overview" | "rewards" | "payments";
  const handleTabChange = (value: string) => {
    const nextTab = value as TabValue;
    if (nextTab !== currentTab) {
      if (nextTab === "overview") {
        overviewDateFilter.onChange(DateRangeOption.ALL_TIME);
      }
      if (nextTab === "payments") {
        paymentsDateFilter.onChange(DateRangeOption.ALL_TIME);
      }
    }

    void navigate({
      search: (prev) => ({
        ...prev,
        tab: nextTab,
        reward: value === "rewards" ? prev.reward : undefined,
      }),
      replace: true,
    });
  };

  const handleRewardChange = (boostId: number | undefined) => {
    void navigate({
      search: (prev) => ({ ...prev, reward: boostId }),
      replace: true,
    });
  };

  if (isPending) {
    return <StandaloneOfferSkeleton />;
  }

  if (error) {
    // Re-throw auth/redirect Responses so the app-level AuthErrorBoundary handles them
    if (error instanceof Response) throw error;

    const isAccessDenied =
      error instanceof TRPCClientError && error.data?.code === "FORBIDDEN";

    return (
      <div className="px-4 py-6 sm:p-6 w-full">
        <ProjectPageHeader title="Offer Details" />
        <div className="mt-4 py-8 text-center">
          <p
            className={
              isAccessDenied ? "text-muted-foreground" : "text-destructive"
            }
          >
            {isAccessDenied
              ? "You do not have access to view this offer."
              : "Failed to load offer details. Please try again."}
          </p>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="px-4 py-6 sm:p-6 w-full">
        <ProjectPageHeader title="Offer Details" />
        <div className="mt-4 py-8 text-center">
          <p className="text-muted-foreground">
            Offer not found. It may have been removed or you don&apos;t have
            access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      {/* Header */}
      <div className="border-b px-4 py-3 sm:px-6 sm:py-4">
        <ProjectPageHeader
          title={offer.offerName}
          className="mb-0"
          subtitle={
            <span className="flex items-center gap-1">
              <Building2 className="inline h-3 w-3 align-[-2px]" />
              {offer.clientName}
            </span>
          }
        />
      </div>

      {/* Content */}
      <div className="flex-1 w-full px-4 py-4 sm:px-6 sm:py-6">
        {/* Landing Page banner */}
        {offer.isLandingPageEnabled && (
          <CreatorStoreBanner
            previewLink={previewLink}
            offerId={offerId}
            orgSlug={orgSlug}
            memberId={memberId}
          />
        )}

        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="mb-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            {hasBoosts && <TabsTrigger value="rewards">Rewards</TabsTrigger>}
            <TabsTrigger value="payments">Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-0 space-y-4">
            <OfferStatsDisplay
              stats={offer.stats}
              showClicks={isLinkOffer(offer) || isPromoOffer(offer)}
              offerCreatedDate={offer.createdDate}
              isLoading={isFetching}
              dateFilter={{
                selectedOption: overviewDateFilter.selectedOption,
                dateRange: overviewDateFilter.dateRange,
                onChange: overviewDateFilter.onChange,
              }}
            />

            {isPromoOffer(offer) && (
              <PromoCodeCard
                offer={offer}
                linkedAffiliateLink={offer.linkedAffiliateLink}
                organizationId={organization.id}
                offerUrl={offerUrl}
                memberId={memberId}
              />
            )}
            {isLinkOffer(offer) && (
              <AffiliateLinkCard
                offer={offer}
                organizationId={organization.id}
                offerUrl={offerUrl}
              />
            )}

            {/* Create Deep Link — shown for link offers with deep linking enabled, but not Landing Page offers */}
            {isLinkOffer(offer) &&
              offer.creatorDeeplink &&
              offerUrl &&
              !offer.isLandingPageEnabled && (
                <div className="flex justify-center">
                  <CreateDeepLinkDialog
                    organizationId={organization.id}
                    offerUrl={offerUrl}
                    affiliateOfferLinkId={offer.id}
                    deepLinkDomains={offer.deepLinkDomains}
                    offerLinkUrl={offer.offerLinkUrl}
                    disabled={offer.status.toUpperCase() !== "ACTIVE"}
                  />
                </div>
              )}

            <PayoutTierDetails
              payoutTiers={offer.payoutTiers}
              offerAutomationConfig={offer.offerAutomationConfig}
              stats={offer.stats}
              showPayoutsOnCreatorPortal={offer.showPayoutsOnCreatorPortal}
            />

            <PayoutBreakdown
              basePayout={offer.basePayout}
              showPayoutsOnCreatorPortal={offer.showPayoutsOnCreatorPortal}
            />

            {/* Share button */}
            <div className="flex justify-center">
              <ShareOfferDialog offer={offer} socialLinks={offer.socialLinks} />
            </div>
          </TabsContent>

          {hasBoosts && (
            <TabsContent value="rewards" className="mt-0">
              <BoostRewardsSection
                boosts={offer.boosts}
                memberId={offer.memberId}
                orgSlug={orgSlug}
                profileSlug=""
                campaignId=""
                interactionRoute={location.pathname}
                selectedBoostId={reward}
                onSelectedBoostChange={handleRewardChange}
                offerId={numericOfferId}
              />
            </TabsContent>
          )}

          <TabsContent value="payments" className="mt-0">
            <PaymentsTab
              payments={offer.payments}
              stats={offer.stats}
              offerCreatedDate={offer.createdDate}
              isLoading={isFetching}
              dateFilter={{
                selectedOption: paymentsDateFilter.selectedOption,
                dateRange: paymentsDateFilter.dateRange,
                onChange: paymentsDateFilter.onChange,
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function CreatorStoreBanner({
  previewLink,
  offerId,
  orgSlug,
  memberId,
}: {
  previewLink: string | null;
  offerId: string;
  orgSlug: string;
  memberId: number;
}) {
  return (
    <Card className="bg-primary/[0.04] mb-4">
      <CardContent className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2 shrink-0">
            <Store className="h-4 w-4 text-primary sm:h-5 sm:w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Landing Page</p>
            <p className="text-xs text-muted-foreground hidden sm:block">
              Personalize your storefront and share it with your audience
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" asChild>
              <Link
                to="/o/$orgSlug/offers/$offerId/customize-landing"
                params={{ orgSlug, offerId }}
                search={{ memberId }}
              >
                <Paintbrush className="h-3.5 w-3.5 mr-1.5" />
                Customize
              </Link>
            </Button>
            {previewLink && (
              <Button variant="outline" size="sm" asChild>
                <a href={previewLink} target="_blank" rel="noopener noreferrer">
                  <Eye className="h-3.5 w-3.5 mr-1.5" />
                  Preview
                </a>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StandaloneOfferSkeleton() {
  return (
    <div className="flex flex-col min-h-full">
      <div className="border-b px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-32 mt-1" />
          </div>
        </div>
      </div>
      <div className="flex-1 w-full px-4 py-4 sm:px-6 sm:py-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
        <Skeleton className="h-40" />
      </div>
    </div>
  );
}
